import type { PhotoFrameItem } from '@/schemas/v2/photo-frame'
import { useEffect, useRef } from 'react'

export const canvasDimensions = { height: 1080, width: 1920 }
// export const canvasDimensions = { height: 1280, width: 720 }
// export const canvasDimensions = { height: 720, width: 480 }

export const useInitCameraStream = (front = false, onPermissionDenied?: () => void) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const onPermissionDeniedRef = useRef(onPermissionDenied)
  onPermissionDeniedRef.current = onPermissionDenied

  const mobileOS = getMobileOS()
  const aspectRatio = mobileOS === 'Android' ? 9 / 16 : undefined

  const initCameraStream = async () => {
    const mediaDevices = navigator.mediaDevices
    const videoPreview = videoRef.current
    if (!mediaDevices) return
    if (!videoPreview) return

    const constraints: MediaStreamConstraints = {
      video: {
        width: { ideal: canvasDimensions.width },
        height: { ideal: canvasDimensions.height },
        facingMode: front ? 'user' : 'environment',
        aspectRatio,
      },
    }

    if (navigator.mediaDevices.enumerateDevices) {
      // async/await で安全に処理（forEach+Promise は never-resolve のリスクがある）
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => [] as MediaDeviceInfo[])
      const matchingDevice = devices.find((d) => d.kind === 'videoinput' && d.label.includes(front ? 'front' : 'back'))
      if (matchingDevice?.deviceId) {
        ;(constraints.video as MediaTrackConstraints).deviceId = matchingDevice.deviceId
      }
    }

    try {
      const stream = await mediaDevices.getUserMedia(constraints)
      // getUserMedia 解決後に要素が DOM から切り離されていないか確認する。
      // オーバーレイが閉じるなどでアンマウントされていた場合は stream を即停止してリソース漏洩を防ぐ。
      if (!videoPreview.isConnected) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }
      videoPreview.srcObject = stream
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotAllowedError') {
        // カメラ許可のキャンセル・拒否: コールバックを呼ぶ
        onPermissionDeniedRef.current?.()
      } else {
        console.error('カメラ起動エラー', e)
      }
    }
  }

  useEffect(() => {
    initCameraStream()

    const video = videoRef.current

    return () => {
      if (video?.srcObject) {
        const stream = video.srcObject as MediaStream
        stream.getTracks().forEach((track) => track.stop())
        video.srcObject = null
      }
    }
  }, [front])

  return videoRef
}

export const getCameraStream = async (front = false) => {
  const mobileOS = getMobileOS()
  const aspectRatio = mobileOS === 'Android' ? 9 / 16 : undefined

  const mediaDevices = navigator.mediaDevices
  if (!mediaDevices) return

  const constraints = {
    width: { ideal: canvasDimensions.width },
    height: { ideal: canvasDimensions.height },
    facingMode: front ? 'user' : 'environment',
    aspectRatio,
  }

  return await mediaDevices.getUserMedia({
    video: constraints,
  })
}

export const preloadImages = async (frames: string[]) => {
  try {
    const loadImage = (imageUrl: string) =>
      new Promise<HTMLImageElement | null>((resolve) => {
        const image = new Image()
        image.crossOrigin = 'Anonymous'
        // URL は加工せずそのまま使う（#3372）。
        // フォトフレーム画像は S3 署名付き URL（?X-Amz-Signature=...）で返るため、
        // キャッシュバスター（?タイムスタンプ）を足すと署名末尾が汚れて 403 SignatureDoesNotMatch になる。
        // 署名付き URL は API を叩くたびにクエリが変わるためキャッシュバスターは不要で、
        // 旧公開 URL（クエリなし）は CDN キャッシュに任せる。
        image.src = imageUrl
        image.onload = () => resolve(image)
        image.onerror = () => resolve(null)
      })

    const promiseList = frames.map(loadImage)

    const resolvedImages = await Promise.all(promiseList)

    return resolvedImages.filter((x) => !!x)
  } catch (error) {
    return []
  }
}

export const isGalaxyDevice = () => {
  if (typeof navigator === 'undefined') {
    return false
  }

  const mobileOS = getMobileOS()
  const isGalaxy = /samsung|galaxy|sm-/i.test(navigator.userAgent)
  return mobileOS === 'Android' && isGalaxy
}

export function getSupportedMineTypeWithCodec() {
  // Safari対応: codecではなくcodecsを使用、iOS/Safari用のコーデックを優先
  const mobileOS = getMobileOS()

  let codecList: string[] = []

  if (mobileOS === 'iOS') {
    // iOS/Safari用のコーデック優先順位
    codecList = [
      'video/mp4;codecs=h264', // Safariで最も互換性が高い
      'video/mp4;codecs=avc1.42E01E', // Safari用のH.264プロファイル
      'video/mp4', // フォールバック
      'video/webm;codecs=vp8', // 最後の手段
    ]
  } else {
    if (isGalaxyDevice()) {
      // Android/その他のブラウザ用
      codecList = [
        'video/mp4;codecs=avc1.42E01E',
        // vp9が一番のおすすめだが、カクカクしているため、avc1.42E01Eを優先している
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/mp4;codecs=h264',
        'video/mp4',
      ]
    } else {
      // Android/その他のブラウザ用
      codecList = [
        // vp9が一番のおすすめだが、カクカクしているため、avc1.42E01Eを優先している
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/mp4;codecs=h264',
        'video/mp4',
      ]
    }
  }

  //サポートを確認
  let supportedCodec = ''

  codecList.every((codec) => {
    if (MediaRecorder.isTypeSupported(codec)) {
      supportedCodec = codec
      return false
    }
    return true
  })

  return supportedCodec
}

export const getMobileOS = () => {
  if (typeof navigator === 'undefined') {
    return ''
  }

  const userAgent = navigator.userAgent
  if (/android/i.test(userAgent)) {
    return 'Android'
  }
  if (/iPad|iPhone|iPod/.test(userAgent) || navigator.maxTouchPoints > 1) {
    return 'iOS'
  }

  return 'Other'
}

export const getOptimalFrameRate = () => {
  const mobileOS = getMobileOS()

  // その他のAndroidデバイスでは30fps
  if (mobileOS === 'Android') {
    return 30
  }

  // iOSでは30fps
  return 30
}

/**
 * ユーザーがフレーム設定ダイアログで非表示にしたレイヤーか（#3622）。
 * クレジット（credit:true）はプレビューでは visibility:hidden だが「ユーザーが隠した」わけではないので
 * style.visibility ではなく data-hidden を見る。撮影・録画の合成ループはこの関数だけで除外を判断する。
 */
export const isLayerHidden = (canvas: Pick<HTMLCanvasElement, 'dataset'>): boolean => canvas.dataset.hidden === 'true'

/** クレジット（透かし）レイヤーか。生成時に data-credit="true" を付けている（#3622） */
export const isCreditLayer = (canvas: Pick<HTMLCanvasElement, 'dataset'>): boolean => canvas.dataset.credit === 'true'

/** フレーム設定ダイアログに列挙する 1 行分 */
export type FrameSettingsItem = {
  /** photoFrame 配列上の元 index（canvas のクラス名 `image_canvas-{index}` と対応） */
  index: number
  fit: boolean
  thumbnailUrl: string
  visible: boolean
}

/**
 * フレーム設定ダイアログに出すレイヤーを選ぶ（#3622）。
 * クレジットはユーザーが表示/非表示を切り替えられないため除外する。
 * index は photoFrame 配列上の位置を保持し、トグル時の canvas 特定に使う。
 */
export const toFrameSettingsItems = (frames: PhotoFrameItem[], visibility: boolean[]): FrameSettingsItem[] =>
  frames.flatMap((frame, index) =>
    frame.credit ? [] : [{ index, fit: frame.fit, thumbnailUrl: frame.file, visible: visibility[index] ?? true }],
  )
